// Rampas de color oficiales de matplotlib para índices de teledetección
// Cada rampa es un array de hexadecimales, cantidad estándar de matplotlib

// Colores ancla (control points) para cada rampa
export const COLOR_RAMPS: Record<string, string[]> = {
  // RdYlGn (11 colores) - para NDVI, GNDVI, NDRE, EVI, MSAVI, ReCI
  RdYlGn: [
    '#a50026',
    '#d73027',
    '#f46d43',
    '#fdae61',
    '#fee08b',
    '#ffffbf',
    '#d9ef8b',
    '#a6d96a',
    '#66bd63',
    '#1a9850',
    '#006837',
  ],
  // Viridis (11 colores, invertida) - para NDMI
  ViridisInv: [
    '#fde725',
    '#b5de2b',
    '#6ece58',
    '#35b779',
    '#1f9e89',
    '#26828e',
    '#31688e',
    '#3e4989',
    '#482878',
    '#440154',
    '#440154',
  ],
  // Blues (9 colores) - para NDWI
  Blues: [
    '#f7fbff',
    '#deebf7',
    '#c6dbef',
    '#9ecae1',
    '#6baed6',
    '#4292c6',
    '#2171b5',
    '#08519c',
    '#08306b',
  ],
};

// Cache de rampas expandidas para evitar recomputar
const EXPANDED_CACHE: Record<string, string[]> = {};

// Interpolación lineal simple en espacio RGB (rápida). Si se requiere mayor fidelidad perceptual
// se podría migrar a interpolación LAB usando una librería externa (no añadida para evitar peso extra).
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const bigint = parseInt(h, 16);
  if (h.length === 6) {
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  }
  // fallback improbable
  return [0, 0, 0];
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) => v.toString(16).padStart(2, '0');
  return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(Math.round(b))}`;
}

/**
 * Genera una rampa de alta resolución (por defecto 256) a partir de colores ancla.
 * Mantiene fidelidad (no altera valores del raster) ya que solo afecta la representación.
 */
export function getExpandedRamp(
  rampKey: keyof typeof COLOR_RAMPS,
  steps = 256
): string[] {
  const cacheKey = `${rampKey}_${steps}`;
  if (EXPANDED_CACHE[cacheKey]) return EXPANDED_CACHE[cacheKey];
  const anchors = COLOR_RAMPS[rampKey];
  if (!anchors || anchors.length === 0) return [];
  if (steps <= anchors.length) return anchors; // nada que expandir

  const result: string[] = new Array(steps);
  const segments = anchors.length - 1;
  for (let i = 0; i < steps; i++) {
    const tGlobal = i / (steps - 1);
    const segFloat = tGlobal * segments;
    const segIndex = Math.min(Math.floor(segFloat), segments - 1);
    const localT = segFloat - segIndex;
    const c1 = hexToRgb(anchors[segIndex]);
    const c2 = hexToRgb(anchors[segIndex + 1]);
    const r = lerp(c1[0], c2[0], localT);
    const g = lerp(c1[1], c2[1], localT);
    const b = lerp(c1[2], c2[2], localT);
    result[i] = rgbToHex(r, g, b);
  }
  EXPANDED_CACHE[cacheKey] = result;
  return result;
}

// Asociación de índice a rampa
export const INDEX_TO_RAMP: Record<string, keyof typeof COLOR_RAMPS> = {
  NDVI: 'RdYlGn',
  GNDVI: 'RdYlGn',
  NDRE: 'RdYlGn',
  EVI: 'RdYlGn',
  MSAVI: 'RdYlGn',
  ReCI: 'RdYlGn',
  NDMI: 'ViridisInv',
  NDWI: 'Blues',
};
